import { resolveComputerHydrationCause } from '#pplx/frontend/apps/ask/ask-input/effort/computerHydration.js';
import { getActiveThreadComputerModeFromSelection, parseStoredComputerEffortPreferences, serializeStoredComputerEffortPreferences, } from '#pplx/frontend/apps/ask/ask-input/effort/computerModeConfigSerializer.js';
import { COMPUTER_EFFORT_PREFERENCES_STORAGE_KEY } from '#pplx/frontend/apps/ask/ask-input/effort/types.js';
import { useQuotaEffortAccessState } from '#pplx/frontend/apps/ask/ask-input/effort/useQuotaEffortAccessState.js';
import { useXplatComputerEffortUiVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useXplatComputerEffortUiVariation.js';
import { useResults } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { hasLocalUserSetting, useLocalUserSettings, } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { getAvailableComputerHarnessModes, getEffectiveComputerHarnessMode, } from '#pplx/frontend/ask-models/hooks/utilities/getSearchModelConfigQuery.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const DEFAULT_COMPUTER_EFFORT_PREFERENCES = {
    version: 1,
    harness_mode: 'standard',
};
const isUnavailableComputerModel = (model, catalogReady, getModelConfig) => catalogReady &&
    model !== SearchModel.ASI &&
    model !== SearchModel.ASI_OPENROUTER &&
    model !== 'pplx_local' &&
    getModelConfig(model) == null;
const getLatestThreadComputerModeSelection = (results) => results.findLast((result) => result.active_computer_harness_mode != null)
    ?.active_computer_harness_mode ?? null;
export const useComputerEffortState = ({ isFollowUp, isLocalModelSelected = false, reason, searchModel, selectedSearchMode, preferencesStorageKey = COMPUTER_EFFORT_PREFERENCES_STORAGE_KEY, defaultPreferences, enabled, }) => {
    const { firstResult, inFlight, lastResult, results } = useResults();
    const isDesktopBuild = Boolean(import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD);
    const { variation: isDesktopEffortUiEnabled } = useXplatComputerEffortUiVariation({
        defaultValue: false,
        skip: !isDesktopBuild,
    });
    const computerEffortEnabled = enabled ?? (!isDesktopBuild || isDesktopEffortUiEnabled);
    const { isLoading: isQuotaAccessLoading, isRestricted: isEffortSelectionRestricted, canEditPreferences, } = useQuotaEffortAccessState({
        enabled: computerEffortEnabled,
    });
    const hydratedFollowUpPreferences = useMemo(() => getActiveThreadComputerModeFromSelection(lastResult?.active_computer_harness_mode ?? getLatestThreadComputerModeSelection(results)), [lastResult?.active_computer_harness_mode, results]);
    const isModeManagedFollowUp = isFollowUp && hydratedFollowUpPreferences !== null;
    const isKnownLegacyFollowUp = isFollowUp &&
        selectedSearchMode === SearchMode.ASI &&
        lastResult !== null &&
        hydratedFollowUpPreferences === null;
    const shouldLoadComputerEffortCatalog = computerEffortEnabled && (!isFollowUp || isModeManagedFollowUp || isKnownLegacyFollowUp);
    const [storedLocalPreferences, setLocalPreferences] = useLocalUserSettings(preferencesStorageKey, defaultPreferences ?? DEFAULT_COMPUTER_EFFORT_PREFERENCES);
    const parsedLocalPreferences = useMemo(() => parseStoredComputerEffortPreferences(storedLocalPreferences), [storedLocalPreferences]);
    const localPreferences = parsedLocalPreferences ?? defaultPreferences ?? DEFAULT_COMPUTER_EFFORT_PREFERENCES;
    const hasValidStoredLocalPreferences = hasLocalUserSetting(preferencesStorageKey) && parsedLocalPreferences !== null;
    const [followUpPreferencesState, setFollowUpPreferencesState] = useState(null);
    const [intentSource, setIntentSource] = useState('inherited');
    const contextUuid = firstResult?.context_uuid;
    const followUpPreferences = followUpPreferencesState !== null && followUpPreferencesState.contextUuid === contextUuid
        ? followUpPreferencesState.preferences
        : null;
    const { models, computer_harness, getModelAllowedReasoningEfforts, getModelConfig, getModelDefaultReasoningEffort, hasLoadedModelConfigFromServer, query: computerEffortCatalogQuery, } = useSearchModels({
        reason,
        enabled: shouldLoadComputerEffortCatalog,
    });
    const harnessModes = useMemo(() => getAvailableComputerHarnessModes(computer_harness?.modes), [computer_harness?.modes]);
    const defaultHarnessMode = getEffectiveComputerHarnessMode(computer_harness?.default_mode ?? DEFAULT_COMPUTER_EFFORT_PREFERENCES.harness_mode, harnessModes);
    const defaultHarnessConfig = computer_harness?.modes[defaultHarnessMode];
    const isComputerEffortCatalogReady = hasLoadedModelConfigFromServer &&
        defaultHarnessConfig != null &&
        getModelConfig(defaultHarnessConfig.orchestrator_model) != null &&
        defaultHarnessConfig.orchestrator_effort != null &&
        getModelAllowedReasoningEfforts(defaultHarnessConfig.orchestrator_model)?.includes(defaultHarnessConfig.orchestrator_effort) === true;
    const isRouterOnlyLegacyFollowUp = isKnownLegacyFollowUp && searchModel === SearchModel.ASI;
    const legacyFollowUpPreferences = useMemo(() => {
        if (!isKnownLegacyFollowUp || !hasLoadedModelConfigFromServer || !searchModel)
            return null;
        if (isRouterOnlyLegacyFollowUp) {
            return computer_harness == null ? null : { version: 1, harness_mode: defaultHarnessMode };
        }
        if (isUnavailableComputerModel(searchModel, isComputerEffortCatalogReady, getModelConfig)) {
            return { version: 1, harness_mode: defaultHarnessMode };
        }
        const defaultEffort = getModelDefaultReasoningEffort(searchModel);
        const allowedEfforts = getModelAllowedReasoningEfforts(searchModel);
        if (!defaultEffort || !allowedEfforts?.includes(defaultEffort))
            return null;
        return {
            version: 1,
            orchestrator_model: searchModel,
            orchestrator_effort: defaultEffort,
        };
    }, [
        getModelAllowedReasoningEfforts,
        getModelConfig,
        getModelDefaultReasoningEffort,
        computer_harness,
        defaultHarnessMode,
        hasLoadedModelConfigFromServer,
        isComputerEffortCatalogReady,
        isKnownLegacyFollowUp,
        isRouterOnlyLegacyFollowUp,
        searchModel,
    ]);
    const usesComputerEffortUi = computerEffortEnabled &&
        (isEffortSelectionRestricted ||
            !isFollowUp ||
            isModeManagedFollowUp ||
            legacyFollowUpPreferences !== null);
    const canEmitComputerModeConfig = usesComputerEffortUi && !isLocalModelSelected && selectedSearchMode === SearchMode.ASI;
    const isLocalModeBootstrapPending = !isFollowUp && canEmitComputerModeConfig && !hasValidStoredLocalPreferences;
    const canBootstrapLocalMode = hasLoadedModelConfigFromServer && computer_harness != null;
    const isLegacyCatalogHydrating = isKnownLegacyFollowUp &&
        computerEffortEnabled &&
        !hasLoadedModelConfigFromServer &&
        (isRouterOnlyLegacyFollowUp || computerEffortCatalogQuery.isFetching);
    const isInitialCatalogError = !hasLoadedModelConfigFromServer &&
        computerEffortCatalogQuery.isError &&
        !computerEffortCatalogQuery.isFetching;
    const isInstantCatalogHydrating = !hasLoadedModelConfigFromServer &&
        !isInitialCatalogError &&
        (isFollowUp ? (followUpPreferences ?? hydratedFollowUpPreferences) : localPreferences)
            ?.harness_mode === 'instant';
    const computerHydrationCause = resolveComputerHydrationCause({
        quotaLoading: !isLocalModelSelected && selectedSearchMode === SearchMode.ASI && isQuotaAccessLoading,
        outOfCreditsRestricted: !isLocalModelSelected &&
            selectedSearchMode === SearchMode.ASI &&
            isEffortSelectionRestricted &&
            (!hasLoadedModelConfigFromServer ||
                computer_harness?.modes.light === null ||
                computer_harness?.modes.light === undefined),
        localModeBootstrapPending: isLocalModeBootstrapPending && !canBootstrapLocalMode,
        catalogUnloaded: isFollowUp && isLegacyCatalogHydrating,
        followupNoLastResult: isFollowUp && (lastResult === null || lastResult === undefined),
        modelConfigUnloaded: (isFollowUp &&
            isModeManagedFollowUp &&
            computerEffortEnabled &&
            !hasLoadedModelConfigFromServer) ||
            (canEmitComputerModeConfig && isInstantCatalogHydrating),
    });
    const isHydrating = computerHydrationCause !== undefined;
    const preferences = useMemo(() => isFollowUp
        ? (followUpPreferences ??
            hydratedFollowUpPreferences ??
            legacyFollowUpPreferences ??
            DEFAULT_COMPUTER_EFFORT_PREFERENCES)
        : isLocalModeBootstrapPending && canBootstrapLocalMode
            ? (defaultPreferences ?? { version: 1, harness_mode: defaultHarnessMode })
            : localPreferences, [
        defaultHarnessMode,
        defaultPreferences,
        followUpPreferences,
        canBootstrapLocalMode,
        hydratedFollowUpPreferences,
        isFollowUp,
        isLocalModeBootstrapPending,
        legacyFollowUpPreferences,
        localPreferences,
    ]);
    const effectivePreferences = useMemo(() => {
        if (!usesComputerEffortUi)
            return preferences;
        if (preferences.harness_mode !== undefined) {
            const harnessMode = getEffectiveComputerHarnessMode(preferences.harness_mode, harnessModes, isInitialCatalogError && preferences.harness_mode === 'instant'
                ? 'light'
                : computer_harness?.default_mode);
            return harnessMode === preferences.harness_mode
                ? preferences
                : { version: 1, harness_mode: harnessMode };
        }
        // Keep the retired pick so the backend can migrate it; otherwise the user
        // would be reset to the default mode before their replacement is considered.
        return isUnavailableComputerModel(preferences.orchestrator_model, isComputerEffortCatalogReady, getModelConfig) && !models?.[preferences.orchestrator_model]?.replaced_by
            ? { version: 1, harness_mode: defaultHarnessMode }
            : preferences;
    }, [
        computer_harness?.default_mode,
        defaultHarnessMode,
        getModelConfig,
        harnessModes,
        isComputerEffortCatalogReady,
        isInitialCatalogError,
        models,
        preferences,
        usesComputerEffortUi,
    ]);
    const preferencesRevisionRef = useRef(0);
    const inFlightStartPreferencesRevisionRef = useRef(0);
    const wasInFlightRef = useRef(inFlight);
    useEffect(() => {
        if (!wasInFlightRef.current && inFlight) {
            inFlightStartPreferencesRevisionRef.current = preferencesRevisionRef.current;
        }
        if (wasInFlightRef.current &&
            !inFlight &&
            inFlightStartPreferencesRevisionRef.current === preferencesRevisionRef.current) {
            setFollowUpPreferencesState(null);
            setIntentSource('inherited');
        }
        wasInFlightRef.current = inFlight;
    }, [inFlight]);
    useEffect(() => setIntentSource('inherited'), [contextUuid]);
    const selection = useMemo(() => {
        if (isEffortSelectionRestricted) {
            return {
                preferences: { version: 1, harness_mode: 'light' },
                intent: 'explicit',
            };
        }
        return { preferences: effectivePreferences, intent: intentSource };
    }, [isEffortSelectionRestricted, effectivePreferences, intentSource]);
    const modeConfig = useMemo(() => {
        if (!canEmitComputerModeConfig || isHydrating)
            return undefined;
        return { ...selection.preferences, intent_source: selection.intent };
    }, [canEmitComputerModeConfig, isHydrating, selection]);
    const handlePreferencesChange = useCallback((nextPreferences) => {
        if (!canEditPreferences)
            return;
        preferencesRevisionRef.current += 1;
        setIntentSource('explicit');
        if (isFollowUp) {
            setFollowUpPreferencesState({ contextUuid, preferences: nextPreferences });
            return;
        }
        setLocalPreferences(serializeStoredComputerEffortPreferences(nextPreferences));
    }, [contextUuid, isFollowUp, setLocalPreferences, canEditPreferences]);
    return {
        computerEffortPreferences: selection.preferences,
        computerEffortEnabled,
        handleComputerEffortPreferencesChange: handlePreferencesChange,
        isComputerModeHydrating: isHydrating,
        computerHydrationCause,
        modeConfig,
        usesComputerEffortUi,
    };
};
//# sourceMappingURL=useComputerEffortState.js.map